/**
 * Modal de Detalhamento de Saída — visão rica e read-only.
 *
 * Exibe: cabeçalho, informações de conferência (conferente, data, divergências),
 * itens com indicação visual de status e, quando houver, registro estruturado
 * de divergências com motivo e observação.
 *
 * Performance:
 *  • select_output e list_output_divergences são disparados em paralelo.
 *  • checked_by_name já vem no payload (subquery em SQL_BASE_SELECT).
 *  • Labels (empresa, armazém) resolvidas via cache local do versa/db.
 */

import api from '../api.js';
import db  from '../../versa/db.js';
import dialog from '../../versa/dialog.js';
import { notify } from '../../versa/msgbox.js';

// ─── Mapeamentos ──────────────────────────────────────────────────────────────

const STATUS_LABEL = {
	10: 'Criada',    20: 'Picking',    30: 'Separada',
	40: 'Conferindo', 50: 'Unpick',   60: 'Conferida',
	65: 'Faturando', 70: 'Faturada', 75: 'Doca Expedição', 80: 'Expedição',
	90: 'Encerrada', 100: 'Cancelada', 999: 'Excluída',
};

const BOX_STATUS_LABEL = { 10: 'Aberta', 20: 'Lacrada', 30: 'Cancelada' };
const BOX_STATUS_COLOR = { 10: '#3b82f6', 20: '#22c55e', 30: '#dc2626' };

const STATUS_COLOR = {
	10: '#9ca3af', 20: '#3b82f6',  30: '#06b6d4',
	40: '#f97316', 50: '#ef4444',  60: '#22c55e',
	65: '#14b8a6', 70: '#0ea5e9', 75: '#8b5cf6', 80: '#6366f1',
	90: '#6b7280', 100: '#dc2626', 999: '#7f1d1d',
};

const DIVERGENCE_REASON_LABEL = {
	FALTA_FISICA:          'Falta física',
	AVARIA:                'Avaria',
	ERRO_PICKING:          'Erro de picking',
	QUANTIDADE_DIVERGENTE: 'Quantidade divergente',
	CORRECAO_OPERACIONAL:  'Correção operacional',
	OUTROS:                'Outros',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function esc(s) {
	if (s == null) return '';
	return String(s)
		.replace(/&/g, '&amp;').replace(/</g, '&lt;')
		.replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function fmtQty(v, dec = 0) {
	if (v == null || v === '') return '—';
	const n = parseFloat(v);
	return isNaN(n) ? '—' : n.toLocaleString('pt-BR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

function fmtDatetime(iso) {
	if (!iso) return '—';
	try {
		const d = new Date(iso);
		const p = n => String(n).padStart(2, '0');
		return `${p(d.getDate())}/${p(d.getMonth()+1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
	} catch { return iso; }
}

function fmtDate(iso) {
	if (!iso) return '—';
	try {
		const [y, m, d] = iso.split('T')[0].split('-');
		return `${d}/${m}/${y}`;
	} catch { return iso; }
}

/** Resolve label de um registro no cache IndexedDB do versa. */
async function dbLabel(store, key) {
	if (!key) return null;
	try {
		const rec = await db.get(store, key);
		if (!rec) return null;
		// versa armazena o label no índice 2 — é o primeiro campo "label" da store
		// Para companies, o campo é 'nick'; para warehouses, é 'name'
		return rec.nick || rec.name || rec.label || null;
	} catch { return null; }
}

// ─── CSS ──────────────────────────────────────────────────────────────────────

const CSS = `<style>
.odet                    { max-width:880px; width:95vw; }
.odet-hdr                { display:flex; align-items:center; gap:.6rem; flex-wrap:wrap; margin-bottom:.8rem; }
.odet-badge-status       { border-radius:4px; color:#fff; font-size:.76rem; font-weight:700;
                           padding:.2rem .55rem; white-space:nowrap; }
.odet-hdr-id             { font-size:1.05rem; font-weight:700; }
.odet-hdr-sep            { color:#bbb; }
.odet-hdr-sub            { color:#666; font-size:.9rem; }
.odet-grid               { display:flex; flex-wrap:wrap; gap:.4rem .8rem; }
.odet-cell               { display:flex; flex-direction:column; min-width:8.5rem; }
.odet-cell.wide          { min-width:16rem; }
.odet-lbl                { font-size:.69rem; color:#999; text-transform:uppercase; letter-spacing:.05em; }
.odet-val                { font-size:.86rem; font-weight:500; color:#1a1a1a; }
.odet-divider            { border:none; border-top:1px solid #e5e7eb; margin:.75rem 0 .6rem; }
.odet-sec-title          { font-size:.72rem; font-weight:700; text-transform:uppercase;
                           letter-spacing:.07em; color:#666; margin-bottom:.45rem; }
.odet-conf-box           { background:#f0fdf4; border:1px solid #bbf7d0; border-radius:6px;
                           padding:.6rem .85rem; margin:.7rem 0; }
.odet-conf-box.has-div   { background:#fffbeb; border-color:#fde68a; }
.odet-badge-conf         { display:inline-flex; align-items:center; gap:.3rem; border-radius:4px;
                           font-size:.8rem; font-weight:600; padding:.22rem .55rem; margin-top:.4rem; }
.odet-badge-conf.ok      { background:#dcfce7; color:#15803d; }
.odet-badge-conf.warn    { background:#fef3c7; color:#92400e; }
.odet-tbl-wrap           { overflow-x:auto; margin-top:.5rem; }
.odet-tbl                { border-collapse:collapse; width:100%; font-size:.81rem; min-width:560px; }
.odet-tbl th             { background:#f3f4f6; border:1px solid #e0e0e0; padding:.28rem .48rem;
                           font-size:.7rem; text-transform:uppercase; letter-spacing:.04em;
                           white-space:nowrap; position:sticky; top:0; }
.odet-tbl td             { border:1px solid #eee; padding:.28rem .48rem; vertical-align:middle; }
.odet-tbl .r             { text-align:right; white-space:nowrap; }
.odet-tbl .c             { text-align:center; }
.odet-row-ok td          { background:#f0fdf4; }
.odet-row-partial td     { background:#fffbeb; }
.odet-row-unchecked td   { background:#fafafa; color:#aaa; }
.odet-qty-ok             { color:#15803d; font-weight:700; }
.odet-qty-partial        { color:#b45309; font-weight:700; }
.odet-div-row td         { background:#fef9ec; border-top:none !important;
                           font-size:.78rem; padding:.18rem .48rem .3rem 1.4rem; color:#555; }
.odet-div-icon           { color:#f97316; margin-right:.2rem; }
.odet-div-reason         { font-weight:600; color:#92400e; }
.odet-div-sep            { color:#d1d5db; margin:0 .22rem; }
.odet-div-note           { font-style:italic; color:#6b7280; }
.odet-div-auth           { color:#15803d; font-weight:600; font-size:.75rem;
                           background:#dcfce7; border-radius:3px; padding:.1rem .3rem; }
.odet-sub                { font-size:.74rem; color:#888; display:block; }
.odet-footer             { display:flex; justify-content:flex-end; margin-top:.9rem; }
.odet-box-badge          { display:inline-block; border-radius:3px; color:#fff; font-size:.72rem;
                           font-weight:700; padding:.15rem .4rem; white-space:nowrap; }
</style>`;

// ─── Construção de HTML ───────────────────────────────────────────────────────

function buildHeader(o, companyNick, whCompNick, whNick) {
	const color = STATUS_COLOR[o.status] || '#9ca3af';
	const label = STATUS_LABEL[o.status] || o.status;
	const refBlock = o.reference
		? `<div class="odet-cell wide"><span class="odet-lbl">Referência</span><span class="odet-val">${esc(o.reference)}</span></div>`
		: '';
	return `
		<div class="odet-hdr">
			<span class="odet-badge-status" style="background:${color}">${esc(label)}</span>
			<span class="odet-hdr-id">Saída #${esc(String(o.id))}</span>
			<span class="odet-hdr-sep">·</span>
			<span class="odet-hdr-sub">Pedido #${esc(String(o.order_id))}</span>
		</div>
		<div class="odet-grid">
			<div class="odet-cell"><span class="odet-lbl">Empresa</span><span class="odet-val">${esc(companyNick || o.company_id)}</span></div>
			<div class="odet-cell"><span class="odet-lbl">Remetente</span><span class="odet-val">${esc(whCompNick || o.warehouse_company_id || '—')}</span></div>
			<div class="odet-cell"><span class="odet-lbl">Armazém</span><span class="odet-val">${esc(whNick || o.warehouse_id || '—')}</span></div>
			<div class="odet-cell"><span class="odet-lbl">Tipo Vol.</span><span class="odet-val">${esc(o.volume_type || '—')}</span></div>
			<div class="odet-cell"><span class="odet-lbl">Volumes</span><span class="odet-val">${o.volumes != null ? esc(String(o.volumes)) : '—'}</span></div>
			<div class="odet-cell"><span class="odet-lbl">Peso (kg)</span><span class="odet-val">${fmtQty(o.weight, 3)}</span></div>
			<div class="odet-cell"><span class="odet-lbl">Criação</span><span class="odet-val">${fmtDatetime(o.created_on)}</span></div>
			<div class="odet-cell"><span class="odet-lbl">Expedição</span><span class="odet-val">${fmtDatetime(o.expedited_on)}</span></div>
			${refBlock}
			${o.total_price != null ? `<div class="odet-cell"><span class="odet-lbl">Preço total</span><span class="odet-val">R$ ${fmtQty(o.total_price, 2)}</span></div>` : ''}
		</div>`;
}

function buildConferenceBox(o, divs) {
	if (!o.checked_at) return '';
	const hasDivs = divs && divs.length > 0;
	const confName = o.checked_by_name || o.checked_by || '—';
	const badge = hasDivs
		? `<span class="odet-badge-conf warn">⚠ ${divs.length} divergência${divs.length > 1 ? 's' : ''} de quantidade</span>`
		: `<span class="odet-badge-conf ok">✓ Conferida sem divergências de quantidade</span>`;
	return `
		<div class="odet-conf-box${hasDivs ? ' has-div' : ''}">
			<div class="odet-sec-title">Conferência</div>
			<div class="odet-grid">
				<div class="odet-cell"><span class="odet-lbl">Conferida por</span><span class="odet-val">${esc(confName)}</span></div>
				<div class="odet-cell"><span class="odet-lbl">Conferida em</span><span class="odet-val">${fmtDatetime(o.checked_at)}</span></div>
			</div>
			${badge}
		</div>`;
}

function buildItems(items, divs) {
	if (!items || items.length === 0)
		return `<p style="color:#aaa;font-style:italic;margin:.5rem 0">Nenhum item encontrado.</p>`;

	const divMap = {};
	if (divs) divs.forEach(d => { divMap[d.output_item_number] = d; });

	const rows = items.map(item => {
		const req = parseFloat(item.quantity_required);
		const sup = item.quantity_supplied != null ? parseFloat(item.quantity_supplied) : null;
		const hasConf = sup !== null;
		const isDivergent = hasConf && sup < req;
		const rowCls = !hasConf ? 'odet-row-unchecked' : (isDivergent ? 'odet-row-partial' : 'odet-row-ok');

		let supCell, diffCell;
		if (!hasConf) {
			supCell  = `<td class="r" title="Não conferido">—</td>`;
			diffCell = `<td class="r">—</td>`;
		} else {
			const diff = sup - req;
			const qCls = isDivergent ? 'odet-qty-partial' : 'odet-qty-ok';
			supCell  = `<td class="r ${qCls}" title="Fornecido">${fmtQty(sup)}</td>`;
			diffCell = diff === 0
				? `<td class="r odet-qty-ok">0</td>`
				: `<td class="r ${qCls}">${diff > 0 ? '+' : ''}${fmtQty(diff)}</td>`;
		}

		const main = `
			<tr class="${rowCls}">
				<td class="c">${esc(String(item.number))}</td>
				<td>${item.product_sku ? `<span style="font-weight:600">${esc(item.product_sku)}</span>` : ''}
					${item.product_name ? `<span class="odet-sub">${esc(item.product_name)}</span>` : ''}</td>
				<td>${esc(item.lot_number || item.lot_id || '—')}</td>
				<td class="c">${fmtDate(item.expiration_date)}</td>
				<td class="r">${fmtQty(req)}</td>
				${supCell}
				${diffCell}
				<td>${esc(item.location_code || item.location_id || '—')}</td>
			</tr>`;

		const div = divMap[item.number];
		if (!div) return main;

		const reasonLabel = DIVERGENCE_REASON_LABEL[div.reason_code] || div.reason_code;
		const authBadge   = div.authorized_by
			? `<span class="odet-div-sep">·</span><span class="odet-div-auth">✓ Autorizado</span>`
			: '';
		const noteBlock   = div.reason_note
			? `<span class="odet-div-sep">·</span><span class="odet-div-note">${esc(div.reason_note)}</span>`
			: '';
		const sub = `
			<tr class="odet-div-row">
				<td></td>
				<td colspan="7">
					<span class="odet-div-icon">↳</span>
					<span class="odet-div-reason">${esc(reasonLabel)}</span>
					${noteBlock}${authBadge}
				</td>
			</tr>`;
		return main + sub;
	});

	return `
		<hr class="odet-divider">
		<div class="odet-sec-title">Itens (${items.length})</div>
		<div class="odet-tbl-wrap">
			<table class="odet-tbl">
				<thead>
					<tr>
						<th class="c">#</th>
						<th>SKU / Produto</th>
						<th>Lote</th>
						<th class="c">Validade</th>
						<th class="r" title="Quantidade requerida">Qt Req</th>
						<th class="r" title="Quantidade conferida / fornecida">Qt Conf</th>
						<th class="r" title="Diferença (Conf − Req)">Dif</th>
						<th>Posição</th>
					</tr>
				</thead>
				<tbody>${rows.join('')}</tbody>
			</table>
		</div>`;
}

function buildPickingBoxes(boxes) {
	if (!boxes || boxes.length === 0) return '';
	const rows = boxes.map(b => {
		const st    = Number(b.status);
		const color = BOX_STATUS_COLOR[st] || '#9ca3af';
		const label = BOX_STATUS_LABEL[st]  || st;
		return `
			<tr>
				<td>${esc(b.code || '—')}</td>
				<td>${esc(b.seal_code || '—')}</td>
				<td class="c"><span class="odet-box-badge" style="background:${color}">${esc(label)}</span></td>
				<td>${esc(b.opened_by_username || b.opened_by || '—')}</td>
				<td class="c">${fmtDatetime(b.opened_at)}</td>
				<td>${esc(b.closed_by_username || b.closed_by || '—')}</td>
				<td class="c">${fmtDatetime(b.closed_at)}</td>
				<td class="c">${b.total_items ?? '—'}</td>
				<td class="r">${b.total_quantity ?? '—'}</td>
			</tr>`;
	}).join('');
	return `
		<hr class="odet-divider">
		<div class="odet-sec-title">Caixas de Picking (${boxes.length})</div>
		<div class="odet-tbl-wrap">
			<table class="odet-tbl">
				<thead>
					<tr>
						<th>Nº Caixa</th>
						<th>Nº Lacre</th>
						<th class="c">Status</th>
						<th>Aberta por</th>
						<th class="c">Aberta em</th>
						<th>Lacrada por</th>
						<th class="c">Lacrada em</th>
						<th class="c">Itens</th>
						<th class="r">Qtd. Total</th>
					</tr>
				</thead>
				<tbody>${rows}</tbody>
			</table>
		</div>`;
}

// ─── Export principal ─────────────────────────────────────────────────────────

/**
 * Abre o modal de detalhamento de saída.
 * @param {string} companyId  UUID da empresa
 * @param {number} outputId   ID da saída
 */
export default async function openOutputDetailModal(companyId, outputId)
{
	// Carrega output, divergências e caixas em paralelo
	const [output, divergences, boxesResp] = await Promise.all([
		api.call(api.select_output, companyId, outputId),
		api.call(api.list_output_divergences, companyId, outputId),
		api.list_output_picking_boxes(companyId, outputId).then(r => r.json()).catch(() => null),
	]);

	if (!output) return;

	const divs  = Array.isArray(divergences) ? divergences : [];
	const boxes = boxesResp?.boxes || (Array.isArray(boxesResp) ? boxesResp : []);

	// Resolve labels do cache local (IndexedDB do versa) — não dispara requests extras
	const [companyNick, whCompNick, whNick] = await Promise.all([
		dbLabel('companies', output.company_id),
		dbLabel('companies', output.warehouse_company_id),
		dbLabel('warehouses', [output.warehouse_company_id, output.warehouse_id]),
	]);

	const html = `
		${CSS}
		<div class="msgbox odet">
			<div class="mb-title">Detalhamento — Saída #${esc(String(outputId))}</div>
			<div class="mb-text">
				${buildHeader(output, companyNick, whCompNick, whNick)}
				${buildConferenceBox(output, divs)}
				${buildItems(output.items, divs)}
				${buildPickingBoxes(boxes)}
				<div class="odet-footer">
					<button class="button bn_N">Fechar</button>
				</div>
			</div>
		</div>`;

	const dlg = dialog.create(html, key =>
	{
		if (key === 'Escape' || key === 'N') dlg.destroy();
	}, /Escape|N/);
}
